import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMembers,
  createMember,
  downloadExcel,
  downloadFilesZip,
  downloadMemberTemplate,
  bulkCreateMembers,
  sendInviteEmails,
  sendPasswordReset,
  deleteMembers,
  taxYearBulkTransition,
  taxYearDataDelete,
} from '../../services/adminService';
import ConfirmModal from '../../components/ConfirmModal';
import './AdminMembersPage.css';

const AdminMembersPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showTaxYearModal, setShowTaxYearModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTaxYear, setFilterTaxYear] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [deletingMembers, setDeletingMembers] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    company: '',
    taxYear: '',
    email: '',
    residentNumber: '',
    sendInvite: false,
  });
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadSendInvite, setBulkUploadSendInvite] = useState(false);
  const [taxYearNewYear, setTaxYearNewYear] = useState('');
  const [taxYearDeleteYear, setTaxYearDeleteYear] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionMessageType, setActionMessageType] = useState('');
  const actionMessageTimeoutRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState('');
  const [confirmDanger, setConfirmDanger] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadMembers();
  }, [searchTerm, filterCompany, filterStatus, filterTaxYear]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterCompany) params.company = filterCompany;
      if (filterStatus) params.status = filterStatus;
      if (filterTaxYear) params.tax_year = filterTaxYear;

      const data = await getMembers(params);
      setMembers(data.members || []);
      setSelectedMembers([]);
    } catch (error) {
      console.error('회원 목록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const showActionMessage = (message, type) => {
    setActionMessage(message);
    setActionMessageType(type);
    if (actionMessageTimeoutRef.current) {
      clearTimeout(actionMessageTimeoutRef.current);
    }
    actionMessageTimeoutRef.current = setTimeout(() => {
      setActionMessage('');
      setActionMessageType('');
    }, 3000);
  };

  const openConfirm = (action, message, isDanger = false) => {
    setConfirmAction(action);
    setConfirmMessage(message);
    setConfirmDanger(isDanger);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmMessage('');
    setConfirmAction('');
    setConfirmDanger(false);
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    try {
      await createMember(newMember);
      showActionMessage('회원이 생성되었습니다.', 'success');
      setShowCreateModal(false);
      setNewMember({
        name: '',
        company: '',
        taxYear: '',
        email: '',
        residentNumber: '',
        sendInvite: false,
      });
      loadMembers();
    } catch (error) {
      showActionMessage('회원 생성에 실패했습니다.', 'error');
      console.error('회원 생성 실패:', error);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkUploadFile) {
      showActionMessage('파일을 선택해주세요.', 'error');
      return;
    }

    try {
      const result = await bulkCreateMembers(bulkUploadFile, bulkUploadSendInvite);
      showActionMessage(`회원 ${result.created || 0}명이 생성되었습니다.`, 'success');
      setShowBulkUploadModal(false);
      setBulkUploadFile(null);
      setBulkUploadSendInvite(false);
      loadMembers();
    } catch (error) {
      showActionMessage('회원 일괄 업로드에 실패했습니다.', 'error');
      console.error('일괄 업로드 실패:', error);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterCompany) params.company = filterCompany;
      if (filterStatus) params.status = filterStatus;
      if (filterTaxYear) params.tax_year = filterTaxYear;

      const blob = await downloadExcel(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `회원정보_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('Excel 다운로드에 실패했습니다.');
      console.error('Excel 다운로드 실패:', error);
    }
  };

  const handleDownloadFilesZip = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterCompany) params.company = filterCompany;
      if (filterStatus) params.status = filterStatus;
      if (filterTaxYear) params.tax_year = filterTaxYear;

      const data = await getMembers(params);
      const memberIds = (data.members || []).map(m => m.id);

      if (memberIds.length === 0) {
        alert('다운로드할 회원이 없습니다.');
        return;
      }

      const blob = await downloadFilesZip(memberIds);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `첨부파일_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('첨부파일 다운로드에 실패했습니다.');
      console.error('첨부파일 다운로드 실패:', error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadMemberTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '회원_일괄_업로드_템플릿.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('Template 다운로드에 실패했습니다.');
      console.error('Template 다운로드 실패:', error);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMembers(members.filter(m => !m.is_admin && !m.is_staff).map(m => m.id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSelectMember = (memberId) => {
    if (selectedMembers.includes(memberId)) {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, memberId]);
    }
  };

  const handleSendInviteEmails = async () => {
    if (selectedMembers.length === 0) {
      showActionMessage('회원을 선택해주세요.', 'error');
      return;
    }

    openConfirm(
      'sendInviteEmails',
      `선택한 ${selectedMembers.length}명에게 초대 메일을 발송하시겠습니까?`
    );
  };

  const performSendInviteEmails = async () => {
    setSendingEmails(true);
    try {
      await sendInviteEmails(selectedMembers);
      showActionMessage('초대 메일이 발송되었습니다.', 'success');
    } catch (error) {
      showActionMessage('초대 메일 발송에 실패했습니다.', 'error');
      console.error('초대 메일 발송 실패:', error);
    } finally {
      setSendingEmails(false);
    }
  };

  const handleSendPasswordResets = async () => {
    if (selectedMembers.length === 0) {
      showActionMessage('회원을 선택해주세요.', 'error');
      return;
    }

    openConfirm(
      'sendPasswordResets',
      `선택한 ${selectedMembers.length}명에게 비밀번호 재설정 메일을 발송하시겠습니까?`
    );
  };

  const handleDeleteMembers = async () => {
    if (selectedMembers.length === 0) {
      showActionMessage('회원을 선택해주세요.', 'error');
      return;
    }

    openConfirm(
      'deleteMembers',
      `선택한 ${selectedMembers.length}명의 회원을 삭제합니다.\n회원 정보 및 작성된 데이터가 모두 삭제됩니다.\n이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?`,
      true
    );
  };

  const performSendPasswordResets = async () => {
    setSendingEmails(true);
    try {
      await sendPasswordReset(selectedMembers);
      showActionMessage('비밀번호 재설정 메일이 발송되었습니다.', 'success');
    } catch (error) {
      showActionMessage('비밀번호 재설정 메일 발송에 실패했습니다.', 'error');
      console.error('비밀번호 재설정 메일 발송 실패:', error);
    } finally {
      setSendingEmails(false);
    }
  };

  const performDeleteMembers = async () => {
    setDeletingMembers(true);
    try {
      await deleteMembers(selectedMembers);
      showActionMessage('회원 삭제가 완료되었습니다.', 'success');
      loadMembers();
    } catch (error) {
      showActionMessage('회원 삭제에 실패했습니다.', 'error');
      console.error('회원 삭제 실패:', error);
    } finally {
      setDeletingMembers(false);
    }
  };

  const handleTaxYearTransition = async () => {
    if (selectedMembers.length === 0) {
      showActionMessage('회원을 선택해주세요.', 'error');
      return;
    }

    if (!taxYearNewYear) {
      showActionMessage('새 과세연도를 입력해주세요.', 'error');
      return;
    }

    openConfirm(
      'taxYearTransition',
      `선택한 ${selectedMembers.length}명의 과세연도를 ${taxYearNewYear}년으로 전환하시겠습니까?`
    );
  };

  const performTaxYearTransition = async () => {
    try {
      await taxYearBulkTransition(selectedMembers, parseInt(taxYearNewYear));
      showActionMessage('과세연도 전환이 완료되었습니다.', 'success');
      setShowTaxYearModal(false);
      setTaxYearNewYear('');
      loadMembers();
    } catch (error) {
      showActionMessage('과세연도 전환에 실패했습니다.', 'error');
      console.error('과세연도 전환 실패:', error);
    }
  };

  const handleTaxYearDataDelete = async () => {
    if (selectedMembers.length === 0) {
      showActionMessage('회원을 선택해주세요.', 'error');
      return;
    }

    if (!taxYearDeleteYear) {
      showActionMessage('삭제할 과세연도를 입력해주세요.', 'error');
      return;
    }

    openConfirm(
      'taxYearDataDelete',
      `선택한 ${selectedMembers.length}명의 ${taxYearDeleteYear}년 데이터를 모두 삭제합니다.\n이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?`,
      true
    );
  };

  const performTaxYearDataDelete = async () => {
    try {
      await taxYearDataDelete(selectedMembers, parseInt(taxYearDeleteYear));
      showActionMessage('과세연도 데이터 삭제가 완료되었습니다.', 'success');
      setShowTaxYearModal(false);
      setTaxYearDeleteYear('');
      loadMembers();
    } catch (error) {
      showActionMessage('과세연도 데이터 삭제에 실패했습니다.', 'error');
      console.error('과세연도 데이터 삭제 실패:', error);
    }
  };

  const handleConfirm = async () => {
    const action = confirmAction;
    closeConfirm();
    if (action === 'sendInviteEmails') {
      await performSendInviteEmails();
    } else if (action === 'sendPasswordResets') {
      await performSendPasswordResets();
    } else if (action === 'deleteMembers') {
      await performDeleteMembers();
    } else if (action === 'taxYearTransition') {
      await performTaxYearTransition();
    } else if (action === 'taxYearDataDelete') {
      await performTaxYearDataDelete();
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      not_accessed: '미접속',
      in_progress: '작성 중',
      submitted: '제출 완료',
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status) => {
    const classMap = {
      not_accessed: 'status-not-accessed',
      in_progress: 'status-in-progress',
      submitted: 'status-submitted',
    };
    return classMap[status] || '';
  };

  const companies = [...new Set(members.map((m) => m.company))].filter(Boolean);
  const taxYears = [...new Set(members.map((m) => m.tax_year).filter(Boolean))].sort((a, b) => b - a);

  return (
    <div className="admin-members-page">
      <div className="page-header">
        <h1>회원 관리</h1>
        <div className="header-actions">
          <button
            onClick={handleDownloadExcel}
            className="excel-download-btn"
          >
            Excel 다운로드
          </button>
          <button
            onClick={handleDownloadFilesZip}
            className="files-download-btn"
          >
            첨부파일 다운로드
          </button>
          <button
            onClick={handleDownloadTemplate}
            className="template-download-btn"
          >
            Template 다운로드
          </button>
          <button
            onClick={() => setShowBulkUploadModal(true)}
            className="bulk-upload-btn"
          >
            회원 일괄 업로드
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="create-btn"
          >
            + 신규 회원 생성
          </button>
        </div>
      </div>
      {actionMessage && (
        <div className={`action-message ${actionMessageType}`}>
          {actionMessage}
        </div>
      )}

      {selectedMembers.length > 0 && (
        <div className="selected-members-actions">
          <div className="bulk-email-actions">
            <button
              onClick={handleSendInviteEmails}
              className="send-invite-btn"
              disabled={sendingEmails}
            >
              {sendingEmails ? '발송 중...' : '초대 메일 발송'}
            </button>
            <button
              onClick={handleSendPasswordResets}
              className="send-password-reset-btn"
              disabled={sendingEmails}
            >
              {sendingEmails ? '발송 중...' : '비밀번호 재발송'}
            </button>
          </div>
          <button
            onClick={handleDeleteMembers}
            className="danger-btn"
            disabled={deletingMembers}
          >
            {deletingMembers ? '삭제 중...' : `회원 삭제 (${selectedMembers.length}명)`}
          </button>
          <button
            onClick={() => setShowTaxYearModal(true)}
            className="tax-year-manage-btn"
          >
            과세연도 관리 ({selectedMembers.length}명)
          </button>
        </div>
      )}

      <div className="filters">
        <div className="filter-group">
          <label>검색</label>
          <input
            type="text"
            placeholder="이름, 이메일로 검색"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>고객사</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
          >
            <option value="">전체</option>
            {companies.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>제출 현황</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">전체</option>
            <option value="not_accessed">미접속</option>
            <option value="in_progress">작성 중</option>
            <option value="submitted">제출 완료</option>
          </select>
        </div>
        <div className="filter-group">
          <label>과세연도</label>
          <select
            value={filterTaxYear}
            onChange={(e) => setFilterTaxYear(e.target.value)}
          >
            <option value="">전체</option>
            {taxYears.map((year) => (
              <option key={year} value={year}>
                {year}년
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">로딩 중...</div>
      ) : (
        <div className="members-table-wrapper">
          <table className="members-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedMembers.length > 0 && selectedMembers.length === members.filter(m => !m.is_admin && !m.is_staff).length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>성함</th>
                <th>회사명</th>
                <th>이메일</th>
                <th>과세연도</th>
                <th>제출 현황</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    회원이 없습니다.
                  </td>
                </tr>
              ) : (
                members.map((member) => {
                  const isAdmin = member.is_admin || member.is_staff;
                  const isSelected = selectedMembers.includes(member.id);
                  return (
                    <tr key={member.id}>
                      <td className="checkbox-col">
                        {!isAdmin && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectMember(member.id)}
                          />
                        )}
                      </td>
                      <td>{member.name}</td>
                      <td>{member.company}</td>
                      <td>{member.email}</td>
                      <td>{member.tax_year || '-'}</td>
                      <td>
                        <span
                          className={`status-badge ${getStatusClass(
                            member.status
                          )}`}
                        >
                          {getStatusLabel(member.status)}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => navigate(`/admin/members/${member.id}`)}
                          className="detail-btn"
                        >
                          상세보기
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>신규 회원 생성</h2>
            <form onSubmit={handleCreateMember}>
              <div className="form-group">
                <label>
                  성함 <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={newMember.name}
                  onChange={(e) =>
                    setNewMember({ ...newMember, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  회사명 <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={newMember.company}
                  onChange={(e) =>
                    setNewMember({ ...newMember, company: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  과세연도 <span className="required">*</span>
                </label>
                <input
                  type="number"
                  value={newMember.taxYear}
                  onChange={(e) =>
                    setNewMember({ ...newMember, taxYear: e.target.value })
                  }
                  required
                  placeholder="예: 2025"
                />
              </div>
              <div className="form-group">
                <label>
                  이메일 주소 <span className="required">*</span>
                </label>
                <input
                  type="email"
                  value={newMember.email}
                  onChange={(e) =>
                    setNewMember({ ...newMember, email: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  주민등록번호 <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={newMember.residentNumber}
                  onChange={(e) =>
                    setNewMember({
                      ...newMember,
                      residentNumber: e.target.value,
                    })
                  }
                  required
                  placeholder="예: 900101-1000001"
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newMember.sendInvite}
                    onChange={(e) =>
                      setNewMember({
                        ...newMember,
                        sendInvite: e.target.checked,
                      })
                    }
                  />
                  초대 메일 발송
                </label>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="cancel-btn"
                >
                  취소
                </button>
                <button type="submit" className="submit-btn">
                  생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkUploadModal && (
        <div className="modal-overlay" onClick={() => setShowBulkUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>회원 일괄 업로드</h2>
            <form onSubmit={handleBulkUpload}>
              <div className="form-group">
                <label>Excel 파일 <span className="required">*</span></label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setBulkUploadFile(e.target.files[0])}
                  required
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={bulkUploadSendInvite}
                    onChange={(e) => setBulkUploadSendInvite(e.target.checked)}
                  />
                  초대 메일 발송
                </label>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowBulkUploadModal(false)}
                  className="cancel-btn"
                >
                  취소
                </button>
                <button type="submit" className="submit-btn">
                  업로드
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTaxYearModal && (
        <div className="modal-overlay" onClick={() => setShowTaxYearModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>과세연도 관리</h2>
            <div className="tax-year-actions">
              <div className="form-group">
                <label>과세연도 일괄 전환</label>
                <div className="inline-input-group">
                  <input
                    type="number"
                    value={taxYearNewYear}
                    onChange={(e) => setTaxYearNewYear(e.target.value)}
                    placeholder="새 과세연도 (예: 2027)"
                  />
                  <button
                    type="button"
                    onClick={handleTaxYearTransition}
                    className="submit-btn"
                  >
                    전환
                  </button>
                </div>
                <p className="help-text">선택한 회원들의 과세연도를 새 연도로 전환하고 전년도 데이터를 복사합니다.</p>
              </div>
              <div className="form-group">
                <label>과세연도 데이터 삭제</label>
                <div className="inline-input-group">
                  <input
                    type="number"
                    value={taxYearDeleteYear}
                    onChange={(e) => setTaxYearDeleteYear(e.target.value)}
                    placeholder="삭제할 과세연도 (예: 2025)"
                  />
                  <button
                    type="button"
                    onClick={handleTaxYearDataDelete}
                    className="danger-btn"
                  >
                    삭제
                  </button>
                </div>
                <p className="help-text danger">선택한 회원들의 특정 과세연도 데이터를 모두 삭제합니다. 이 작업은 되돌릴 수 없습니다.</p>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowTaxYearModal(false)}
                className="cancel-btn"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        open={confirmOpen}
        title="확인"
        message={confirmMessage}
        isDanger={confirmDanger}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default AdminMembersPage;
